import { useEffect, useRef, useState } from "react";
import { useApp } from "../vault/ctx";
import { Thumb } from "../vault/Common";
import { useAuth } from "../../features/auth/context/AuthContext";
import { apiGet } from "../../features/shared/apiClient";
import { updateWorkspace } from "../../features/settings/settingsApi";
import { readImageAsDataUrl } from "../../features/shared/imageUpload";
import { listDomains, addDomain, verifyDomain, deleteDomain, type CustomDomain } from "../../features/vault/vaultApi";

/** Subset of the workspace doc GET /workspaces/:id returns that this page reads. */
interface WsBrand {
  name: string;
  slug: string;
  logo?: string;
  primaryColor?: string;
}

const ACCENTS = ["#A07E3E", "#1F5B4E", "#7B2D3E", "#233A63", "#211F1B"];

export default function BrandingPage() {
  const { setClient } = useApp();
  const { activeWorkspace, refreshWorkspaces } = useAuth();
  const wsId = activeWorkspace?.workspaceId;

  const [ws, setWs] = useState<WsBrand | null>(null);
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [domainInput, setDomainInput] = useState("");
  const [adding, setAdding] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!wsId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiGet<WsBrand>(`/workspaces/${wsId}`),
      listDomains().then((r) => r.domains).catch(() => [] as CustomDomain[]),
    ])
      .then(([w, ds]) => { if (!cancelled) { setWs(w); setDomains(ds); } })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Could not load branding"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [wsId]);

  const pickLogo = async (file?: File) => {
    if (!file || !wsId) return;
    setError(null);
    try {
      const logo = await readImageAsDataUrl(file, 256);
      const u = await updateWorkspace(wsId, { logo });
      setWs((w) => (w ? { ...w, logo: u.logo } : w));
      void refreshWorkspaces();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save logo");
    }
  };

  const saveColor = async (c: string) => {
    if (!wsId || (ws?.primaryColor ?? "").toLowerCase() === c.toLowerCase()) return;
    const prev = ws;
    setWs((w) => (w ? { ...w, primaryColor: c } : w)); // optimistic
    setError(null);
    try {
      const u = await updateWorkspace(wsId, { primaryColor: c });
      setWs((w) => (w ? { ...w, primaryColor: u.primaryColor } : w));
      void refreshWorkspaces();
    } catch (e) {
      setWs(prev); // revert
      setError(e instanceof Error ? e.message : "Could not save color");
    }
  };

  const addDomainHandler = async () => {
    const domain = domainInput.trim();
    if (!domain || adding) return; // Enter key bypasses the button's disabled state — guard re-entry
    setAdding(true);
    setError(null);
    try {
      const d = await addDomain(domain);
      setDomains((ds) => [...ds, d]);
      setDomainInput("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add domain");
    } finally {
      setAdding(false);
    }
  };

  const removeDomain = async (d: CustomDomain) => {
    if (!confirm(`Disconnect ${d.domain}?`)) return;
    try {
      await deleteDomain(d._id);
      setDomains((ds) => ds.filter((x) => x._id !== d._id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not remove domain");
    }
  };

  const accent = ws?.primaryColor;
  const activeDomain = domains.find((d) => d.status === "active") ?? domains[0];
  const previewHost = activeDomain?.domain ?? "share.jewelitt.com";
  const previewSlug = ws?.slug ?? activeWorkspace?.workspaceSlug ?? "workspace";

  return (
    <>
      <div className="page-title">Branding</div>
      <div className="page-sub">Make every share page, watermark and email look like your house — not ours.</div>
      <div className="two-col" style={{ marginTop: 18 }}>
        <div>
          <div className="card">
            <h3>Logo</h3>
            <div className="hint">Shown on client share pages and as the render watermark.</div>
            <div style={{ display: "flex", gap: 14, marginTop: 14 }}>
              <div style={{ flex: 1, border: "1.5px dashed var(--border-strong)", borderRadius: 12, padding: 18, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, background: "var(--surface-2)" }}>
                {ws?.logo ? (
                  <img src={ws.logo} alt="Workspace logo" style={{ height: 34, maxWidth: 150, objectFit: "contain" }} />
                ) : (
                  <div style={{ height: 34, display: "flex", alignItems: "center", fontWeight: 700, letterSpacing: 1, color: "var(--muted)" }}>{ws?.name ?? "Your brand"}</div>
                )}
                <span className="hint">{ws?.logo ? "Custom logo · shown on client share pages" : "No logo uploaded yet"}</span>
                <div><button className="btn ghost" style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => fileRef.current?.click()}>Replace</button></div>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => pickLogo(e.target.files?.[0])} />
              </div>
            </div>
          </div>
          <div className="card">
            <h3>Brand colors</h3>
            <div className="field"><label>Accent</label>
              <div className="swatches">
                {ACCENTS.map((c) => <div key={c} className={"swatch" + ((accent ?? "").toLowerCase() === c.toLowerCase() ? " on" : "")} style={{ background: c }} onClick={() => saveColor(c)}></div>)}
              </div>
            </div>
          </div>
          <div className="card">
            <h3>Custom domain</h3>
            <div className="field"><label>Domain</label>
              <input
                type="text"
                placeholder="viewer.yourbrand.com"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void addDomainHandler(); } }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button className="btn ghost" style={{ padding: "6px 12px", fontSize: 12 }} onClick={addDomainHandler} disabled={adding}>{adding ? "Adding…" : "Add domain"}</button>
            </div>
            {loading ? (
              <div className="hint" style={{ marginTop: 12 }}>Loading…</div>
            ) : domains.length === 0 ? (
              <div className="hint" style={{ marginTop: 12 }}>No custom domains yet.</div>
            ) : (
              domains.map((d) => (
                <DomainRow
                  key={d._id}
                  d={d}
                  onChange={(u) => setDomains((ds) => ds.map((x) => (x._id === u._id ? u : x)))}
                  onRemove={() => removeDomain(d)}
                />
              ))
            )}
          </div>
          {error && <div className="hint" style={{ color: "var(--red)" }}>{error}</div>}
        </div>
        <div className="card" style={{ alignSelf: "start", position: "sticky", top: 0 }}>
          <h3>Live preview — client share page</h3>
          <div className="hint">What your clients see when they open your link.</div>
          <div style={{ border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", marginTop: 12 }}>
            <div style={{ height: 34, background: "var(--surface-2)", display: "flex", alignItems: "center", gap: 6, padding: "0 12px" }}>
              <span className="code" style={{ fontSize: 10.5 }}>{previewHost}/s/{previewSlug}</span>
            </div>
            <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid var(--border)" }}>
              {ws?.logo ? (
                <img src={ws.logo} alt="" style={{ height: 26, maxWidth: 118, objectFit: "contain" }} />
              ) : (
                <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text)" }}>{ws?.name ?? "Your brand"}</span>
              )}
              <span style={{ flex: 1 }}></span><span className="badge gold" style={accent ? { background: accent, borderColor: accent, color: "#fff" } : undefined}>Download all</span>
            </div>
            <div style={{ padding: "14px 18px" }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Engagement — 2027 Bridal picks</div>
              <div className="hint" style={{ marginBottom: 10 }}>Preview · shared by {ws?.name ?? "your workspace"}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <Thumb art="ringGold" bg="bgWarm" style={{ height: 64, borderRadius: 8 }} />
                <Thumb art="diaSol" bg="bgCool" style={{ height: 64, borderRadius: 8 }} />
                <Thumb art="ringRose" bg="bgDark" style={{ height: 64, borderRadius: 8 }} />
              </div>
            </div>
          </div>
          <button className="btn primary" style={{ width: "100%", justifyContent: "center", marginTop: 14 }} onClick={() => setClient(true)}>Open full client-view preview →</button>
        </div>
      </div>
    </>
  );
}

/** One real custom domain — active shows the verified badge; pending shows the DNS records + Verify. */
function DomainRow({ d, onChange, onRemove }: { d: CustomDomain; onChange: (d: CustomDomain) => void; onRemove: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const verify = async () => {
    setBusy(true);
    setErr(null);
    try { onChange(await verifyDomain(d._id)); }
    catch (e) { setErr(e instanceof Error ? e.message : "Couldn't verify — check the DNS record is live, then try again."); }
    finally { setBusy(false); }
  };
  // dns.txt is added by the backend; type it locally to avoid a shared-type edit.
  const txt = (d.dns as { txt?: { name: string; value: string } }).txt;

  if (d.status === "active") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <span className="conn-dot"></span>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--green)" }}>DNS verified</span>
        <span className="hint">· {d.domain} · {d.dns.type} → {d.dns.value} · SSL issued</span>
        <button className="btn ghost" style={{ padding: "6px 12px", fontSize: 12 }} onClick={onRemove}>Remove</button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 13 }}>{d.domain}</strong>
        <span className="hint" style={{ color: "var(--muted)" }}>· Pending DNS</span>
        <span style={{ flex: 1 }}></span>
        <button className="btn ghost" style={{ padding: "6px 12px", fontSize: 12 }} onClick={verify} disabled={busy}>{busy ? "Checking…" : "Verify"}</button>
        <button className="btn ghost" style={{ padding: "6px 12px", fontSize: 12 }} onClick={onRemove}>Remove</button>
      </div>
      <div className="hint" style={{ marginTop: 8 }}>Add this DNS record at your registrar:</div>
      <div className="code" style={{ display: "block", marginTop: 6, padding: "8px 10px", fontSize: 11 }}>{d.dns.type} &nbsp; {d.dns.name} &nbsp;→&nbsp; {d.dns.value}</div>
      {txt && <div className="code" style={{ display: "block", marginTop: 6, padding: "8px 10px", fontSize: 11 }}>TXT &nbsp; {txt.name} &nbsp;→&nbsp; {txt.value}</div>}
      <div className="hint" style={{ marginTop: 6 }}>{d.dns.note}</div>
      {err && <div className="hint" style={{ marginTop: 6, color: "var(--red)" }}>{err}</div>}
    </div>
  );
}
